import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup, Validators, FormControl, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService } from '@auth/auth.service';
import { AuthNoticeService } from '@auth/auth-notice/auth-notice.service';
import { SflBaseComponent } from '@shared/components/sfl-base/sfl-base.component';
import { AppConstants } from '@shared/constants/app.constant';
import { AdministrationService } from '@entities/administration/administration.service';
import { ConfirmPasswordValidator } from '@auth/register/confirm-password.validator';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'kt-change-password',
  templateUrl: './change-password.component.html',
  encapsulation: ViewEncapsulation.None
})
export class ChangePasswordComponent extends SflBaseComponent implements OnInit, OnDestroy {
  changePasswordForm: FormGroup;
  currentUserEmail: string;
  AppConstants = AppConstants;
  cardTitle = 'Change Password';
  cardSubTitle = '';
  constructor(
    private readonly authService: AuthService,
    private readonly adminService: AdministrationService,
    public readonly authNoticeService: AuthNoticeService,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.initChangePasswordForm();
    this.getCurrentUserEmail();
  }

  ngOnDestroy(): void {
    this.isSubmitting = false;
    this.authNoticeService.setNotice('');
  }

  private getCurrentUserEmail() {
    this.currentUserEmail = localStorage.getItem('userEmail');
    if (!this.currentUserEmail) {
      // If email not found in localStorage, try to get from auth service
      this.authService
        .getCurrentUser()
        .then((user) => {
          this.currentUserEmail = user?.user?.email;
        })
        .catch(() => {
          this.authNoticeService.setNotice('Unable to get user information', 'danger');
        });
    }
  }

  initChangePasswordForm() {
    this.changePasswordForm = new FormGroup(
      {
        newPassword: new FormControl('', [Validators.required, Validators.minLength(AppConstants.requiredMinLength), Validators.maxLength(AppConstants.passwordMaxLength)]),
        confirmPassword: new FormControl('', [Validators.required, Validators.minLength(AppConstants.requiredMinLength), Validators.maxLength(AppConstants.passwordMaxLength)])
      },
      { validators: [this.passwordValidator] }
    );
  }

  passwordValidator: ValidatorFn = (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (!newPassword || !confirmPassword) {
      return null; // don’t validate if one is missing
    }

    return newPassword === confirmPassword ? null : { passwordNotMatch: true };
  };

  /**
   * Form Submit
   */
  submit() {
    console.log(this.changePasswordForm);
    if (!this.changePasswordForm) {
      return;
    }

    const controls = this.changePasswordForm.controls;

    // Check form validation
    if (this.changePasswordForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    if (!this.currentUserEmail) {
      this.authNoticeService.setNotice('User email not found. Please login again.', 'danger');
      return;
    }

    this.isSubmitting = true;

    const newPassword = controls.newPassword.value;
    const confirmPassword = controls.confirmPassword.value;

    this.subscriptionManager.add(
      this.adminService
        .updateUserPassword(this.currentUserEmail, newPassword, confirmPassword)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          })
        )
        .subscribe(
          () => {
            this.authNoticeService.setNotice('Password updated successfully', 'success');
            setTimeout(() => {
              this.router.navigateByUrl(this.appRoutes.LOGIN);
            }, 3000);
          },
          (error) => {
            const errorMessage = error?.error?.message || 'Password update failed. Please try again with a strong password';
            this.authNoticeService.setNotice(errorMessage, 'danger');
          }
        )
    );
  }

  /**
   * Navigate back to login
   */
  goBack() {
    this.router.navigateByUrl(this.appRoutes.LOGIN);
  }
}
